import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  ConsentChannel,
  MarketingPerson,
} from "@shared-core/data/marketing-types";
import {
  formatShortDateOrDash,
  formatTimeOrDash,
} from "@shared-core/domain/formatters";
import { MarketingNav } from "../app/components/domain/marketing/MarketingNav";
import { consentVariant } from "../app/components/domain/marketing/marketingDisplay";
import { Badge } from "../app/components/primitives/badge";
import { Input } from "../app/components/primitives/input";
import { Switch } from "../app/components/primitives/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../app/components/primitives/table";

const CONSENT_CHANNELS: { key: ConsentChannel; label: string }[] = [
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "direct_mail", label: "Direct mail" },
  { key: "enclosure", label: "Enclosure" },
];

function fmtDateTime(iso: string): string {
  return `${formatShortDateOrDash(iso)}, ${formatTimeOrDash(iso)}`;
}

export function MarketingConsentPage() {
  const dataAdapter = useDataAdapter();
  const [people, setPeople] = useState<MarketingPerson[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [savingPersonId, setSavingPersonId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const load = async (opts?: { isCancelled?: () => boolean }) => {
    setLoading(true);
    setError(false);
    try {
      const peopleRes = await dataAdapter.listMarketingPeople({
        q: query || undefined,
        pageSize: 200,
      });
      if (!opts?.isCancelled?.()) setPeople(peopleRes.items);
    } catch (e) {
      console.error("marketing.consent.load.failed", e);
      if (!opts?.isCancelled?.()) setError(true);
    } finally {
      if (!opts?.isCancelled?.()) setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    load({ isCancelled: () => cancelled });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataAdapter, query]);

  const handleToggleEmailConsent = async (person: MarketingPerson) => {
    const current =
      person.consent.find((c) => c.channel === "email")?.state ?? "allowed";
    const next = current === "allowed" ? "suppressed" : "allowed";
    setSavingPersonId(person.id);
    setSaveError(null);
    try {
      await dataAdapter.recordConsentChange(person.id, {
        channel: "email",
        state: next,
      });
      await load();
    } catch (e) {
      console.error("marketing.consent.record_change.failed", e);
      setSaveError(
        `Couldn't update email consent for ${person.name}. Please try again.`,
      );
    } finally {
      setSavingPersonId(null);
    }
  };

  return (
    <div className="space-y-6">
      <MarketingNav active="consent" />

      <div>
        <h1 className="text-2xl mb-1">Consent</h1>
        <p className="text-sm text-muted-foreground">
          Platform-owned, per-channel consent. Mailchimp unsubscribes flow in as
          an inbound signal that suppresses email only. Toggle the Email switch
          to opt someone out (or back in) directly — e.g. when they ask by email
          or phone — without needing to change it in Mailchimp first.
        </p>
      </div>

      <div className="relative w-full sm:w-80">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search people by name, email, company…"
          className="pl-9"
        />
      </div>

      {saveError && (
        <div className="text-sm p-3 border border-red-200 bg-red-50 rounded-lg text-red-600">
          {saveError}
        </div>
      )}

      {loading && (
        <div className="text-sm text-muted-foreground">
          Loading consent ledger…
        </div>
      )}

      {error && !loading && (
        <div className="text-sm p-8 text-center border border-border rounded-lg text-red-600">
          Couldn't load the consent ledger. Please try again.
        </div>
      )}

      {!loading && !error && people.length === 0 && (
        <div className="text-sm p-8 text-center border border-border rounded-lg text-muted-foreground">
          No people match your search.
        </div>
      )}

      {!loading && !error && people.length > 0 && (
        <div className="rounded-lg border border-border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Person</TableHead>
                {CONSENT_CHANNELS.map((c) => (
                  <TableHead key={c.key}>{c.label}</TableHead>
                ))}
                <TableHead>Source</TableHead>
                <TableHead>Last changed</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {people.map((p) => {
                const emailRec = p.consent.find((c) => c.channel === "email");
                const lastChanged = p.consent.reduce(
                  (latest, c) =>
                    c.changed_at > latest ? c.changed_at : latest,
                  p.consent[0]?.changed_at ?? "",
                );
                return (
                  <TableRow key={p.id}>
                    <TableCell>
                      <div className="font-medium text-foreground">
                        {p.name}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {p.company_name ?? "Standalone"}
                        {p.email ? ` · ${p.email}` : ""}
                      </div>
                    </TableCell>
                    {CONSENT_CHANNELS.map((ch) => {
                      const rec = p.consent.find((c) => c.channel === ch.key);
                      const state = rec?.state ?? "allowed";
                      if (ch.key !== "email") {
                        return (
                          <TableCell key={ch.key}>
                            <Badge variant={consentVariant(state)}>
                              {state}
                            </Badge>
                          </TableCell>
                        );
                      }
                      // Email is the only channel with a real per-person
                      // consent read today (see recordConsentChange's
                      // comment) — editable here; the others stay
                      // read-only until the list endpoint carries their
                      // state too.
                      return (
                        <TableCell key={ch.key}>
                          <div className="flex items-center gap-2">
                            <Switch
                              checked={state === "allowed"}
                              disabled={savingPersonId === p.id}
                              onCheckedChange={() =>
                                handleToggleEmailConsent(p)
                              }
                              aria-label={
                                state === "allowed"
                                  ? `Opt ${p.name} out of email marketing`
                                  : `Opt ${p.name} in to email marketing`
                              }
                            />
                            <Badge variant={consentVariant(state)}>
                              {state}
                            </Badge>
                          </div>
                        </TableCell>
                      );
                    })}
                    <TableCell className="text-sm text-muted-foreground capitalize">
                      {emailRec?.source ?? "—"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {lastChanged ? fmtDateTime(lastChanged) : "—"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
